import { ReplaySubject } from "rxjs";
import {
  Video,
  VideoInternal,
  MetricLogic,
  Image,
  Framework,
} from "../interfaces";
import { UserFirebaseService } from "./user-firebase";
import * as Sentry from "@sentry/browser";
import { capitalizeFirstLetter } from "../helpers/utils";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

class AdminFirebaseController {
  private metricsSubject: ReplaySubject<MetricLogic[]>;
  private frameworksSubject: ReplaySubject<Framework[]>;
  private metrics: MetricLogic[];
  private frameworks: Framework[];
  private userTruthsSubject: ReplaySubject<Object>;
  private discardListSubject: ReplaySubject<Set<string>>;
  public frameSubject: ReplaySubject<Image[]> = new ReplaySubject<Image[]>(1);
  private questions: Object[];
  public keypoints: string[] = [
    "club_midpoint",
    "club_grip_shaft",
    "left_wrist",
    "club_heel",
    "club_toe",
    "right_wrist",
    "left_shoulder",
    "right_shoulder",
    "left_hip",
    "right_hip",
    "left_ear",
    "right_ear",
    "cap_top",
    "cap_peak_centre",
    "cap_peak_right",
    "cap_peak_left",
    "left_ankle",
    "right_ankle",
    "left_knee",
    "right_knee",
    "golf_ball",
    "left_toes",
    "right_toes",
    "left_elbow",
    "right_elbow",
    "nose",
    "chin",
    "c7_vertebra",
    "tailbone",
    "belly_button",
    "end_of_club"
  ]; //,'nose','right_bicep','left_eye','right_eye','chin']

  public localAnnotationAdjustmentSubject: ReplaySubject<string> =
    new ReplaySubject<string>(1);
  private quizMetricsSubject: ReplaySubject<Object[]> = new ReplaySubject<
    Object[]
  >(1);

  public initialized: boolean = false;
  public advanced: boolean = false;
  private allGolferListSubjectInitialized: boolean = false;
  private quizMetricsSubjectInitialized: boolean = false;
  private allGolferListSubject: ReplaySubject<Object> =
    new ReplaySubject<Object>(1);
  private allVideoListSummarySubject: ReplaySubject<Video[]>;

  private metricMapping: Object;
  private metricFriendlyIdToFirestoreId: Object = null;
  private video_summaries: Video[];
  //set of existing summaries documents
  private existing_summaries: Set<string> = new Set();

  init() {
    if (!this.initialized) {
      this.initialized = true;
      UserFirebaseService.init();
      //this.initQuizMetricsSubject()
    }
  }

  async populateMetricsFriendlyIdToFirestoreId() {
    if (!this.metricFriendlyIdToFirestoreId) {
      this.metricFriendlyIdToFirestoreId = {};
      const metricsInternalCollection = collection(
        UserFirebaseService.firestore_swee_prod,
        "metrics_internal"
      );
      const docs = await getDocs(metricsInternalCollection);
      docs.forEach((doc) => {
        const docdata = doc.data();
        this.metricFriendlyIdToFirestoreId[docdata["id"]] = doc.id;
      });
    }
  }

  public async getIdealImages(line: number, club: string) {
    //access ideal_images collection and find all that match
    const idealImagesCollection = collection(
      UserFirebaseService.firestore_default,
      "ideal_images"
    );
    const q = query(
      idealImagesCollection,
      where("line", "==", line),
      where("club", "==", club)
    );
    const docs = await getDocs(q);
    const images = [];
    docs.forEach((doc) => {
      images.push(doc.data());
    });
    return images;
  }

  public getQuizMetricsSubject() {
    this.initQuizMetricsSubject(); //if already initialized, it returns immediately

    return this.quizMetricsSubject;
  }

  public getAllGolferListSubject() {
    if (!this.allGolferListSubjectInitialized) {
      this.allGolferListSubjectInitialized = true;
      UserFirebaseService.userSubject.subscribe((user) => {
        if (UserFirebaseService.snapshots["allGolfers"])
          UserFirebaseService.snapshots["allGolfers"]();
        console.log("admin init");
        //this will fail if user doesn't have permissions...
        if (user && user.userType == "admin") {
          try {
            console.log("getting all golfers");

            //listen to all docs from golfers1 to golfers50
            //so need a variabe that loops from 1 to 50
            //and then a listener for each one
            const values = Array.from({ length: 50 }, (_, index) => index + 1);
            const golferListMap = {};
            ["default", "dev", "prod"].forEach((environment) => {
              const firestore = UserFirebaseService.getFirestore(environment);
              const websiteCollection = collection(firestore, "website");
              values.forEach((value) => {
                // Your code here, using the variable 'value' as the current value
                //do cutdown version based off website golfers document
                const golferDocRef = doc(
                  websiteCollection,
                  "golfers" + value.toString()
                );
                onSnapshot(golferDocRef, (doc) => {
                  if (!doc.exists()) return;
                  const website_golfers = doc.data()["idToData"];
                  const website_golfer_ids = Object.keys(website_golfers);

                  website_golfer_ids.forEach((id) => {
                    if (id.startsWith("api_")) return;
                    if (id.startsWith("apiUNDERSCORE")) return;
                    const golfer = website_golfers[id];
                    golfer.environment = environment;
                    if (!golfer.firestoreid) golfer.firestoreid = id;
                    if (golfer.lastVideoUpload)
                      golfer.lastVideoUpload = new Date(
                        golfer.lastVideoUpload.seconds * 1000
                      );
                    try {
                      if (
                        golfer.createdAt &&
                        typeof golfer.createdAt !== "string"
                      ) {
                        golfer.createdAt = new Date(
                          golfer.createdAt.seconds * 1000
                        ).toISOString();
                      }
                    } catch (e) {
                      console.log("createdAt error", e, golfer);
                    }
                    golfer.subscriptionSummary =
                      UserFirebaseService.getSubscriptionSummary(golfer);
                    golferListMap[id] = golfer;
                  });

                  //const golferList:Golfer[] = Object.values(golferListMap)
                  //golferList.sort((a,b)=>(a.name?a.name.toLowerCase():'')<(b.name?b.name.toLowerCase():'')?-1:1).slice()
                  this.allGolferListSubject.next(golferListMap);
                });
              });
            });
          } catch (e) {
            console.log("error");
            console.log(e);
          }
        }
      });
    }
    return this.allGolferListSubject;
  }

  async getMetricFirestoreId(firestore_id: string) {
    try {
      const metricsInternalCollection = collection(
        UserFirebaseService.firestore_swee_dev,
        "metrics_internal"
      );
      const docRef = doc(metricsInternalCollection, firestore_id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;

      let metric = docSnap.data();
      metric.firestoreid = docSnap.id;
      return metric;
    } catch (e) {
      console.log("getMetric error");
      console.log(e);
      Sentry.captureException(e);
      Sentry.captureMessage("getMetric error");
    }
  }

  async getMetric(metric_id: string) {
    try {
      const metricsInternalCollection = collection(
        UserFirebaseService.firestore_swee_dev,
        "metrics_internal"
      );
      const q = query(
        metricsInternalCollection,
        where("id", "==", metric_id),
        limit(1)
      );
      const docs = await getDocs(q);
      if (docs.empty) return null;
      if (docs.size == 1) {
        let metric: MetricLogic = docs.docs[0].data() as MetricLogic;
        metric.firestoreid = docs.docs[0].id;
        return metric;
      }
    } catch (e) {
      console.log("getMetric error");
      console.log(e);
      Sentry.captureException(e);
      Sentry.captureMessage("getMetric error");
    }
  }

  updateFramework(firestoreid: string, toUpdate: Object) {
    try {
      const frameworksCollection = collection(
        UserFirebaseService.firestore_swee_dev,
        "frameworks"
      );
      const docRef = doc(frameworksCollection, firestoreid);
      setDoc(docRef, toUpdate, { merge: true });
      return;
    } catch (e) {
      Sentry.captureMessage("updateFramework error");
      Sentry.captureMessage(e);
      console.log(e);
      console.log("updateFramework error");
    }
  }

  async getFramework(frameworkId: string) {
    try {
      const frameworksCollection = collection(
        UserFirebaseService.firestore_swee_dev,
        "frameworks"
      );
      const docRef = doc(frameworksCollection, frameworkId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        let framework = docSnap.data() as Framework;
        return framework;
      }
    } catch (e) {
      console.log("getMetric error");
      console.log(e);
      Sentry.captureException(e);
      Sentry.captureMessage("getMetric error");
    }
  }

  displayFix(fix: Object, includeValues: boolean = true): string {
    try {
      if (!this.metricMapping) return "";
      const rule = this.metricMapping[fix["fix"]].rules.filter(
        (rule) => rule.id == fix["value"]
      )[0];
      return (
        "line " +
        this.metricMapping[fix["fix"]].line +
        " " +
        this.metricMapping[fix["fix"]].name +
        (fix["value"] == "not_visible" || rule.penalty == 0 || !includeValues
          ? ""
          : " " + rule.text)
      );
    } catch (e) {
      console.log("displayFix error");
      console.log(e);
      console.log(fix);
      return "";
    }
  }

  displayMetric(metric: MetricLogic) {
    try {
      return "line " + metric.line + " " + metric.name;
    } catch (e) {
      console.log("displayMetric error");
      console.log(e);
      return "";
    }
  }

  displayFramework(
    framework: Framework,
    excludeFix: string = "",
    inclNameIfApplicable: boolean = false
  ) {
    if (!framework) return "";
    console.log("display framework ", framework.name);
    const fixList = framework.framework
      .filter((a) => a["fix"] != excludeFix)
      .sort((a, b) =>
        this.displayFix(a) < this.displayFix(b)
          ? -1
          : this.displayFix(a) > this.displayFix(b)
          ? 1
          : 0
      )
      .map((fixId) => capitalizeFirstLetter(this.displayFix(fixId, false)))
      .join(", ");
    if (inclNameIfApplicable && framework.name)
      return framework.name + "..." + fixList;
    return fixList;
  }

  updateMetric(firestoreid: string, toUpdate: Object) {
    try {
      const metricsInternalCollection = collection(
        UserFirebaseService.firestore_swee_dev,
        "metrics_internal"
      );
      const docRef = doc(metricsInternalCollection, firestoreid);
      setDoc(docRef, toUpdate, { merge: true });
      //delete annotations property from toUpdate if it exists
      if (toUpdate["annotations"]) delete toUpdate["annotations"];
      if (toUpdate["clip"]) delete toUpdate["clip"];
      const metricsCollection = collection(
        UserFirebaseService.firestore_swee_dev,
        "metrics"
      );
      const docRef2 = doc(metricsCollection, firestoreid);

      return setDoc(docRef2, toUpdate, { merge: true });
    } catch (e) {
      Sentry.captureMessage("updateMetric error");
      Sentry.captureMessage(e);
      console.log(e);
      console.log("updateMetric error");
    }
  }

  async updateStorageFile(
    filename: string,
    path: string,
    filepath: string,
    needThumbnail: boolean = false
  ) {
    const bucket = "ninelines-media";
    const bucketRef = UserFirebaseService.storageService.refFromURL(
      "gs://" + bucket
    );
    const storage_path = path + "/" + filename;
    const fileRef = bucketRef.child(storage_path);
    await fileRef.put(filepath);
    if (needThumbnail) {
      //update metadata to stay generate-thumbnail: true
      await fileRef.updateMetadata({ generateThumbnail: "true" });
    }
    return;
  }

  updateReviewNote(
    environment: string,
    golferid: string,
    firestoreid: string,
    metricid: string,
    notes: string
  ) {
    let obj = {};
    obj["metrics." + metricid + ".reviewer"] = notes;
    console.log(obj);
    const firestore =
      environment == "prod"
        ? UserFirebaseService.firestore_swee_prod
        : environment == "dev"
        ? UserFirebaseService.firestore_swee_dev
        : UserFirebaseService.firestore_default;
    const golfersCollection = collection(firestore, "golfers");
    const golferDocRef = doc(golfersCollection, golferid);
    const videosCollection = collection(golferDocRef, "videos");
    const videoDocRef = doc(videosCollection, firestoreid);
    updateDoc(videoDocRef, obj).catch((e) => console.log(e));
  }

  setGraphicsError(
    environment: string,
    golferid: string,
    firestoreid: string,
    metricid: string,
    isError: boolean
  ) {
    let obj = {};
    obj["metrics." + metricid + ".reviewer_graphic"] = isError;
    console.log(obj);
    const golfersCollection = collection(
      UserFirebaseService.getFirestore(environment),
      "golfers"
    );
    const golferDocRef = doc(golfersCollection, golferid);
    const videosCollection = collection(golferDocRef, "videos");
    const videoDocRef = doc(videosCollection, firestoreid);
    updateDoc(videoDocRef, obj).catch((e) => console.log(e)); //setDoc doesn't work here
    console.log("graphics error now set");
  }

  getStatisticsAll() {
    return new Promise((resolve) => {
      getDoc(
        doc(
          collection(UserFirebaseService.getFirestore("default"), "misc"),
          "statistics"
        )
      )
        .then((doc) => resolve(doc.data()))
        .catch((error) => console.log(error));
    });
  }

  getMetricMapping(): Promise<Object> {
    console.log("METRIC MAPPING", this.metricMapping);
    if (this.metricMapping) {
      return new Promise((resolve) => resolve(this.metricMapping));
    } else {
      // Initialize Firestore
      const firestore = UserFirebaseService.firestore_swee_dev;

      // Create a reference to the 'metrics_internal' collection
      const metricsCollection = collection(firestore, "metrics_internal");

      // Get the documents
      return getDocs(metricsCollection).then((docs) => {
        this.metricMapping = {};
        docs.forEach((doc) => {
          let docdata = doc.data();
          this.metricMapping[docdata.id] = docdata;
          this.metricMapping[docdata.id]["m_id"] = doc.id;
          this.metricMapping[docdata.id]["line_name"] =
            "Line " + docdata.line + " " + docdata.name;
        });
        return this.metricMapping;
      });
    }
  }

  fillSummary(
    query_recent_default,
    query_recent_swee_dev,
    query_recent_swee_prod,
    subject
  ) {
    let videos: Video[] = []; //only gets called once, so doesn't need to default to anything
    if (UserFirebaseService.snapshots["allVideoListSummary_default"])
      UserFirebaseService.snapshots["allVideoListSummary_default"]();
    if (UserFirebaseService.snapshots["allVideoListSummary_dev"])
      UserFirebaseService.snapshots["allVideoListSummary_dev"]();
    if (UserFirebaseService.snapshots["allVideoListSummary_prod"])
      UserFirebaseService.snapshots["allVideoListSummary_prod"]();
    var that = this;

    UserFirebaseService.snapshots["allVideoListSummary_default"] = onSnapshot(
      query_recent_default,
      (querySnapshot) => {
        console.log("videos changed");
        console.log(querySnapshot);
        const changes = querySnapshot.docChanges();

        changes.forEach(function (change) {
          //if the parent begins with api_ then it's a SWEE golfer and we don't want to show it because these are on prod now
          if (change.doc.ref.parent.parent.id.startsWith("api_")) return;
          if (change.type === "removed" || change.type === "modified") {
            videos = videos.filter((v) => v.firestoreid != change.doc.id);
          }
          if (
            change.type == "added" &&
            !(change.doc.id in that.existing_summaries)
          ) {
            //Seems to be necessary because sometimes adds come through for existing ones
            videos.push(
              UserFirebaseService.convertDocToVideo("default", change.doc)
            );
            that.existing_summaries.add(change.doc.id);
          }
          if (change.type === "modified") {
            videos.push(
              UserFirebaseService.convertDocToVideo("default", change.doc)
            );
          }
        });
        this.video_summaries = videos.slice();

        subject.next(this.video_summaries);
      },
      (error) => {
        Sentry.captureMessage(
          "error in videoUploadsSummary snapshot listener admin"
        );
        Sentry.captureException(error);
        console.log("videoUploadsSummary error", error);
      }
    );

    UserFirebaseService.snapshots["allVideoListSummary_prod"] = onSnapshot(
      query_recent_swee_prod,
      (querySnapshot) => {
        console.log("videos changed");
        console.log(querySnapshot);
        const changes = querySnapshot.docChanges();

        changes.forEach(function (change) {
          if (change.type === "removed" || change.type === "modified") {
            videos = videos.filter((v) => v.firestoreid != change.doc.id);
          }
          if (
            change.type == "added" &&
            !(change.doc.id in that.existing_summaries)
          ) {
            //Seems to be necessary because sometimes adds come through for existing ones
            videos.push(
              UserFirebaseService.convertDocToVideo("prod", change.doc)
            );
            that.existing_summaries.add(change.doc.id);
          }
          if (change.type === "modified") {
            videos.push(
              UserFirebaseService.convertDocToVideo("prod", change.doc)
            );
          }
        });
        this.video_summaries = videos.slice();

        subject.next(this.video_summaries);
      },
      (error) => {
        Sentry.captureMessage(
          "error in videoUploadsSummary snapshot listener admin"
        );
        Sentry.captureException(error);
        console.log("videoUploadsSummary error", error);
      }
    );

    UserFirebaseService.snapshots["allVideoListSummary_dev"] = onSnapshot(
      query_recent_swee_dev,
      (querySnapshot) => {
        console.log("videos changed");
        console.log(querySnapshot);
        const changes = querySnapshot.docChanges();

        changes.forEach(function (change) {
          if (change.type === "removed" || change.type === "modified") {
            videos = videos.filter((v) => v.firestoreid != change.doc.id);
          }
          if (
            change.type == "added" &&
            !(change.doc.id in that.existing_summaries)
          ) {
            //Seems to be necessary because sometimes adds come through for existing ones
            videos.push(
              UserFirebaseService.convertDocToVideo("dev", change.doc)
            );
            that.existing_summaries.add(change.doc.id);
          }
          if (change.type === "modified") {
            videos.push(
              UserFirebaseService.convertDocToVideo("dev", change.doc)
            );
          }
        });
        this.video_summaries = videos.slice();

        subject.next(this.video_summaries);
      },
      (error) => {
        Sentry.captureMessage(
          "error in videoUploadsSummary snapshot listener admin"
        );
        Sentry.captureException(error);
        console.log("videoUploadsSummary error", error);
      }
    );
  }

  initAllVideoListSummarySubject() {
    console.log("initializing all video list summary subject");
    UserFirebaseService.userSubject.subscribe(() => {
      const query_recent_default = query(
        query(
          collectionGroup(
            UserFirebaseService.firestore_default,
            "video_summaries"
          ),
          orderBy("createdAt", "desc")
        ),
        limit(4000)
      );
      const query_recent_swee_dev = query(
        query(
          collectionGroup(
            UserFirebaseService.firestore_swee_dev,
            "video_summaries"
          ),
          orderBy("createdAt", "desc")
        ),
        limit(4000)
      );
      const query_recent_swee_prod = query(
        query(
          collectionGroup(
            UserFirebaseService.firestore_swee_prod,
            "video_summaries"
          ),
          orderBy("createdAt", "desc")
        ),
        limit(4000)
      );
      this.fillSummary(
        query_recent_default,
        query_recent_swee_dev,
        query_recent_swee_prod,
        this.allVideoListSummarySubject
      );
    });
  }

  getAllVideoListSummarySubject() {
    if (!this.initialized) this.init();
    if (!this.allVideoListSummarySubject) {
      this.allVideoListSummarySubject = new ReplaySubject<Video[]>(1);
      if (UserFirebaseService.auth.currentUser) {
        this.initAllVideoListSummarySubject();
      }
    }
    return this.allVideoListSummarySubject;
  }

  getGolferVideoListSummarySubject(environment: string, golferid: string) {
    if (!this.initialized) this.init();
    /* Create a subject that will return video summaries for a particular golfer */
    //Create the subject
    const subject = new ReplaySubject<Object>(1);
    //Create the query
    const firestore =
      environment == "prod"
        ? UserFirebaseService.firestore_swee_prod
        : environment == "dev"
        ? UserFirebaseService.firestore_swee_dev
        : UserFirebaseService.firestore_default;
    const golfersCollection = collection(firestore, "golfers");
    const golferDocRef = doc(golfersCollection, golferid);
    const videoSummariesCollection = collection(
      golferDocRef,
      "video_summaries"
    );
    const q = query(videoSummariesCollection, orderBy("createdAt", "desc"));

    //Create the listener
    const listener = onSnapshot(q, (querySnapshot) => {
      const videos: Video[] = [];
      querySnapshot.forEach((doc) => {
        videos.push(UserFirebaseService.convertDocToVideo(environment, doc));
      });
      subject.next({ videos: videos, listener: listener });
    });
    return subject;
  }

  updateMetricTruth(
    environment: string,
    golferid: string,
    firestoreid: string,
    metricid: string,
    truth: string,
    original: string,
    video_truth: Object
  ) {
    let obj = {};
    obj["metrics." + metricid + ".truth"] = truth;
    obj["metrics." + metricid + ".truthoriginal"] = original;
    obj["metrics." + metricid + ".truthreviewer"] =
      UserFirebaseService.auth.currentUser.email;
    console.log(obj);
    const golfersCollection = collection(
      UserFirebaseService.getFirestore(environment),
      "golfers"
    );
    const golferDocRef = doc(golfersCollection, golferid);
    const videosCollection = collection(golferDocRef, "videos");
    const videoDocRef = doc(videosCollection, firestoreid);
    updateDoc(videoDocRef, obj).catch((e) => console.log(e)); //DOES NOT WORK IF YOU TRY TO SETDOC
    //No longer updating this

    const videoSummariesCollection = collection(
      UserFirebaseService.getFirestore(environment),
      "golfers",
      golferid,
      "video_summaries"
    );
    const videoSummaryDocRef = doc(videoSummariesCollection, firestoreid);
    updateDoc(videoSummaryDocRef, { reviewed: true });

    //Also update the quiz answer from that person
    //Not sending original across because trying to use amplitude flow only for quiz questions for now
    this.answerQuizQuestion(
      environment,
      golferid,
      firestoreid,
      metricid.substring(2),
      truth,
      null,
      null,
      video_truth
    );
  }

  async getClips() {
    const clipsCollection = collection(
      UserFirebaseService.firestore_swee_dev,
      "misc"
    );
    const docRef = doc(clipsCollection, "clips");
    const docSnap = await getDoc(docRef);
    return docSnap.data();
  }

  getMetricsSubject() {
    this.init();
    if (!this.metricsSubject) {
      this.metricsSubject = new ReplaySubject<MetricLogic[]>(1);
      if (UserFirebaseService.auth.currentUser) {
        this.initMetricsSubject();
      }
    }
    return this.metricsSubject;
  }

  getFrameworksSubject() {
    this.init();
    if (!this.frameworksSubject) {
      this.frameworksSubject = new ReplaySubject<Framework[]>(1);
      if (UserFirebaseService.auth.currentUser) {
        this.initFrameworksSubject();
      }
    }
    return this.frameworksSubject;
  }

  grantFreeAccess(golferid: string) {
    //alert to get the endDate
    const endDate = prompt("Enter end date (YYYY-MM-DD)");
    const endDateDate = new Date(endDate);
    const golfersCollection = collection(
      UserFirebaseService.firestore_default,
      "golfers"
    );
    const golferDocRef = doc(golfersCollection, golferid);
    updateDoc(golferDocRef, {
      subscriptionStatus: "active",
      subscriptionPlan: "annual",
      subscriptionPlanDisplayName: "Annual",
      subscriptionAutoRenews: false,
      subscriptionPeriodEndDate: endDateDate,
    });
  }

  initMetricsSubject() {
    console.log("in init metrics subject");
    if (UserFirebaseService.snapshots["metrics_internal"])
      UserFirebaseService.snapshots["metrics_internal"]();
    const metricsInternalCollection = collection(
      UserFirebaseService.firestore_swee_prod,
      "metrics_internal"
    );
    UserFirebaseService.snapshots["metrics_internal"] = onSnapshot(
      metricsInternalCollection,
      (querySnapshot) => {
        const metrics = [];
        querySnapshot.docs.forEach((doc) => {
          const docdata = doc.data();

          //{"angle":90,"line":0,"radio_header":"shoulder_alignment","label":"Shoulder alignment","answers":[{"color":"danger","value":"too_open","label":"Too open"},{"color":"warning","value":"slightly_too_open","label":"Slightly too open"},{"color":"success","value":"perfect","label":"About right"},{"color":"warning","value":"slightly_too_closed","label":"Slightly too closed"},{"color":"danger","value":"too_closed","label":"Too closed"}]},
          const metric: MetricLogic = {
            m_id: doc.id,
            club: docdata["club"],
            order: docdata["order"],
            video_angle: docdata["video_angle"] ?? 0,
            line: docdata["line"],
            id: docdata["id"],
            name: capitalizeFirstLetter(docdata["name"]),
            rules: docdata["rules"].map((r) => {
              return {
                maxValue: r["maxValue"],
                minValue: r["minValue"],
                color:
                  r["penalty"] == 1
                    ? "danger"
                    : r["penalty"] == 0.5
                    ? "warning"
                    : "success",
                value: r["id"],
                id: r["id"],
                text: capitalizeFirstLetter(r["text"]),
                fix: r["fix"],
                consequence: r["impact"],
                context: r["problem"],
              };
            }),
          };
          if (!(docdata["quiz"] == false)) metrics.push(metric);
        });
        this.metrics = metrics;
        this.metricsSubject.next(this.metrics);
      }
    );
  }

  initFrameworksSubject() {
    if (UserFirebaseService.snapshots["frameworks"])
      UserFirebaseService.snapshots["frameworks"]();
    const frameworksCollection = collection(
      UserFirebaseService.firestore_swee_dev,
      "frameworks"
    );
    UserFirebaseService.snapshots["frameworks"] = onSnapshot(
      frameworksCollection,
      (querySnapshot) => {
        const frameworks = [];
        querySnapshot.docs.forEach((doc) => {
          const docdata = doc.data();
          if (docdata["exclude"]) return;
          const framework: Framework = {
            exclude: docdata["exclude"] ?? false,
            framework: docdata["framework"],
            club: docdata["club"],
            video_angle: docdata["video_angle"] ?? 0,
            id: doc.id,
            name: capitalizeFirstLetter(docdata["name"]),
            description: docdata["description"],
          };
          frameworks.push(framework);
        });
        this.frameworks = frameworks;
        console.log("publishing to frameworks subject");
        this.frameworksSubject.next(this.frameworks);
      }
    );
  }

  getFrameworkMatch(framework: Framework, metrics: Object) {
    let match_score = 0;
    let num = 0;
    for (const fix of framework.framework) {
      const metric = metrics[fix["fix"]];
      if (metric) {
        num += 1;
        const penalty = metric["penalty"];
        if (fix["value"] == "perfect") match_score += 0.5 * penalty;
        else {
          //figure if if same direction or not
          const answerid = metric["answerid"];
          //find associated red

          //first find matching rule
          if (!this.metricMapping[fix["fix"]]) {
            console.log(
              "no metric mapping for",
              fix["fix"],
              this.metricMapping
            );
          }
          const rule = this.metricMapping[fix["fix"]].rules.filter(
            (rule) => rule.id == answerid
          )[0];
          if (!rule) continue;
          const valueToUse = rule["associated_red"]
            ? rule["associated_red"]
            : rule["id"];
          match_score += penalty * (fix["value"] == valueToUse ? 1 : -1);
        }
      }
    }
    if (num == 0) return -1;
    return match_score / num;
  }

  getUserTruthsSubject(): ReplaySubject<Object> {
    this.init();
    if (!this.userTruthsSubject) {
      this.userTruthsSubject = new ReplaySubject<Object>(1);
      if (UserFirebaseService.auth.currentUser) {
        this.initUserTruthsSubject();
      }
    }
    return this.userTruthsSubject;
  }

  initUserTruthsSubject() {
    const userTruths = {};
    ["dev", "prod", "legacy"].forEach((environment) => {
      if (UserFirebaseService.snapshots["userTruths_" + environment])
        UserFirebaseService.snapshots["userTruths_" + environment]();
      const quizzesCollection = collection(
        doc(
          collection(
            doc(
              collection(
                UserFirebaseService.getFirestore(environment),
                "website"
              ),
              "truth"
            ),
            "users"
          ),
          UserFirebaseService.auth.currentUser.email == "2charlesj@gmail.com"
            ? "maltongue@xtra.co.nz"
            : UserFirebaseService.auth.currentUser.email
        ),
        "quizzes"
      );

      UserFirebaseService.snapshots["userTruths_" + environment] = onSnapshot(
        quizzesCollection,
        (docs) => {
          if (docs) {
            docs.forEach((doc) => {
              const docdata = doc.data();
              userTruths[doc.id] = docdata["answered"];
            });
          }
          this.userTruthsSubject.next(userTruths);
        }
      );
    });
  }

  getDiscardListSubject(): ReplaySubject<Set<string>> {
    this.init();
    if (!this.discardListSubject) {
      this.discardListSubject = new ReplaySubject<Set<string>>(1);
      if (UserFirebaseService.auth.currentUser) {
        this.initDiscardListSubject();
      }
    }
    return this.discardListSubject;
  }

  initDiscardListSubject() {
    let discards: Set<string> = new Set();
    ["dev", "prod", "legacy"].forEach((environment) => {
      if (UserFirebaseService.snapshots["discard_" + environment])
        UserFirebaseService.snapshots["discard_" + environment]();
      const discardsCollection = collection(
        UserFirebaseService.getFirestore(environment),
        "website"
      );
      const discardsDocRef = doc(discardsCollection, "discards");
      UserFirebaseService.snapshots["discard_" + environment] = onSnapshot(
        discardsDocRef,
        (doc) => {
          const docdata = doc.data();

          for (var key in docdata) {
            if (docdata[key]["reason"] >= 0) discards.add(key);
          }
          this.discardListSubject.next(discards);
        },
        (error) => {
          console.log(error);
          Sentry.captureMessage("discards error");
        }
      );
    });
  }

  setFrameSubject(environment: string, golfer_id: string, video_id: string) {
    if (UserFirebaseService.snapshots["frame"])
      UserFirebaseService.snapshots["frame"]();
    this.frameSubject.next([]); //Clear anything that might be there

    if (video_id) {
      const firestore =
        environment == "prod"
          ? UserFirebaseService.firestore_swee_prod
          : environment == "dev"
          ? UserFirebaseService.firestore_swee_dev
          : UserFirebaseService.firestore_default;
      const imagesCollection = collection(
        firestore,
        "golfers",
        golfer_id,
        "videos",
        video_id,
        "images"
      );
      const q = query(imagesCollection, orderBy("frame", "asc"));
      UserFirebaseService.snapshots["frame"] = onSnapshot(
        q,
        (querySnapshot) => {
          const images: Image[] = [];
          querySnapshot.forEach(function (docSnap) {
            console.log("docSnap", docSnap);
            images.push(
              UserFirebaseService.convertDocToImage(environment, docSnap)
            );
          });
          this.frameSubject.next(images);
        }
      );
    }
  }

  setLineTruth(
    videoInternal: VideoInternal,
    line: number,
    frame: number,
    value: string
  ) {
    if (value == "y" || value == "n") {
      console.log("setLineTruth", videoInternal, line, frame, value);
      let truth =
        videoInternal && videoInternal.truth ? videoInternal.truth : {};
      console.log("truth", truth);
      if (!("lines" in truth)) {
        console.log("adding lines array");
        truth["lines"] = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
      }
      truth["lines"][line][frame] = value;
      console.log(truth["lines"][line][frame]);
      const internalDocRef = doc(
        collection(
          UserFirebaseService.getFirestore(videoInternal.environment),
          "golfers",
          videoInternal.golferid,
          "videos",
          videoInternal.firestoreid,
          "internal"
        ),
        "internal"
      );
      console.log("have internal doc ref");
      setDoc(internalDocRef, { truth: truth }, { merge: true });
      console.log("set doc");
    } else {
      console.log("undefined value...just a UI bug with ionic I think");
    }
  }

  async annotateLiveFeedbackImage(
    keypoint: string,
    ref,
    offsetX: number,
    offsetY: number,
    imageindex: number,
    imageFileName?: string
  ) {
    const fieldPath = `annotated_keypoints_by_image.${imageindex}.${keypoint}`;
    const toUpdate: any = {
      [fieldPath]: [offsetX, offsetY],
    };

    // If this keypoint is currently flagged as discrepant for this image,
    // remove it as soon as a manual annotation is provided.
    if (imageFileName) {
      const docSnap = await getDoc(ref);
      const docData: any = docSnap.data() || {};
      const discrepancyMap = docData.keypoint_discrepancies || {};
      const existingForImage = Array.isArray(discrepancyMap[imageFileName])
        ? discrepancyMap[imageFileName]
        : [];
      if (existingForImage.includes(keypoint)) {
        const filtered = existingForImage.filter(
          (discrepantKeypoint: string) => discrepantKeypoint !== keypoint
        );
        toUpdate.keypoint_discrepancies = {
          ...discrepancyMap,
          [imageFileName]: filtered,
        };
      }
    }

    await updateDoc(ref, toUpdate);
  }

  async removeLiveFeedbackKeypointDiscrepancy(
    keypoint: string,
    ref,
    imageFileName: string
  ) {
    if (!imageFileName) {
      return;
    }

    const docSnap = await getDoc(ref);
    const docData: any = docSnap.data() || {};
    const discrepancyMap = docData.keypoint_discrepancies || {};
    const existingForImage = Array.isArray(discrepancyMap[imageFileName])
      ? discrepancyMap[imageFileName]
      : [];

    if (!existingForImage.includes(keypoint)) {
      return;
    }

    const filtered = existingForImage.filter(
      (discrepantKeypoint: string) => discrepantKeypoint !== keypoint
    );
    const updatedDiscrepancyMap = {
      ...discrepancyMap,
      [imageFileName]: filtered,
    };
    await updateDoc(ref, {
      keypoint_discrepancies: updatedDiscrepancyMap,
    });
  }

  annotateImage(
    keypoint: string,
    ref,
    offsetX: number,
    offsetY: number,
    occluded: boolean
  ) {
    let keypoints = {};
    keypoints[keypoint] = [offsetX, offsetY];
    const toUpdate = { annotated_keypoints: keypoints, annotation_status: {} };
    toUpdate["annotation_status"][keypoint] = occluded ? 2 : 3;

    //ref.set(toUpdate,{merge:true})
    setDoc(ref, toUpdate, { merge: true });
    this.localAnnotationAdjustmentSubject.next(ref.id + keypoint);
  }

  removeRecordingAngleAccuracy(video: Video, videoInternal: VideoInternal) {
    let truth = videoInternal && videoInternal.truth ? videoInternal.truth : {};
    if (!("video_angle" in truth)) {
      truth["video_angle"] = {};
    }
    delete truth["video_angle"][video.video_angle];
    const internalDocRef = doc(
      collection(
        UserFirebaseService.getFirestore(videoInternal.environment),
        "golfers",
        video.golferid,
        "videos",
        video.firestoreid,
        "internal"
      ),
      "internal"
    );
    updateDoc(internalDocRef, { truth: truth });
  }

  removeHandednessAccuracy(videoInternal: VideoInternal) {
    let truth = videoInternal && videoInternal.truth ? videoInternal.truth : {};
    delete truth["handedness"];
    const internalDocRef = doc(
      collection(
        UserFirebaseService.getFirestore(videoInternal.environment),
        "golfers",
        videoInternal.golferid,
        "videos",
        videoInternal.firestoreid,
        "internal"
      ),
      "internal"
    );
    updateDoc(internalDocRef, { truth: truth });
  }

  setHandednessAccuracy(
    video: Video,
    videoInternal: VideoInternal,
    status: boolean
  ) {
    console.log("set handedness accuracy");
    let truth = videoInternal && videoInternal.truth ? videoInternal.truth : {};
    truth["handedness"] = status
      ? video.handedness
      : video.handedness == "right"
      ? "left"
      : "right";
    console.log("truth to set", truth);
    const internalDocRef = doc(
      collection(
        UserFirebaseService.getFirestore(video.environment),
        "golfers",
        video.golferid,
        "videos",
        video.firestoreid,
        "internal"
      ),
      "internal"
    );
    setDoc(internalDocRef, { truth: truth }, { merge: true });
  }

  deleteFrames(environment: string, golfer_id: string, video_id: string) {
    const imagesCollection = collection(
      UserFirebaseService.getFirestore(environment),
      "golfers",
      golfer_id,
      "videos",
      video_id,
      "images"
    );
    getDocs(imagesCollection).then((docs) => {
      docs.docs.map((doc) => deleteDoc(doc.ref));
    });
  }

  setRecordingAngleAccuracy(
    video: Video,
    videoInternal: VideoInternal,
    status: boolean
  ) {
    console.log("setting recording angle");
    let truth = videoInternal && videoInternal.truth ? videoInternal.truth : {};
    if (!("video_angle" in truth)) {
      truth["video_angle"] = {};
    }
    truth["video_angle"][video.video_angle] = status;
    console.log("truth to set", truth);
    const internalDocRef = doc(
      collection(
        UserFirebaseService.getFirestore(video.environment),
        "golfers",
        video.golferid,
        "videos",
        video.firestoreid,
        "internal"
      ),
      "internal"
    );
    setDoc(internalDocRef, { truth: truth }, { merge: true });
  }

  updateLineReviewNote(video: Video, line: number, notes: string) {
    video.lineReviewer[line] = notes;
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(video.environment),
        "golfers",
        video.golferid,
        "videos"
      ),
      video.firestoreid
    );
    setDoc(docRef, { lineReviewer: video.lineReviewer }, { merge: true }).catch(
      (e) => console.log(e)
    );
  }

  updateSpeedReviewNote(video: Video, notes: string) {
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(video.environment),
        "golfers",
        video.golferid,
        "videos"
      ),
      video.firestoreid
    );
    updateDoc(docRef, { speedReviewer: notes }).catch((e) => console.log(e));
  }

  setRecordingIssueTruth(environment, golferid, firestoreid, code, truth) {
    let obj = {};
    obj[code] = truth;
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(environment),
        "golfers",
        golferid,
        "videos"
      ),
      firestoreid
    );
    setDoc(docRef, { recordingIssueTruth: obj }, { merge: true }).catch((e) =>
      console.log(e)
    );
  }

  setTagTruth(
    environment: string,
    golferid: string,
    firestoreid: string,
    tagId: string,
    status: boolean
  ) {
    let obj = {};
    obj[tagId] = status;
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(environment),
        "golfers",
        golferid,
        "videos"
      ),
      firestoreid
    );
    setDoc(docRef, { tagTruth: obj }, { merge: true }).catch((e) =>
      console.log(e)
    );
  }

  setSuggestedTagTruth(
    environment: string,
    golferid: string,
    firestoreid: string,
    tagId: string,
    priority: number
  ) {
    let obj = {};
    obj[priority] = tagId;
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(environment),
        "golfers",
        golferid,
        "videos"
      ),
      firestoreid
    );
    setDoc(docRef, { suggestedTagTruth: obj }, { merge: true }).catch((e) =>
      console.log(e)
    );
  }

  deleteVideo(environment: string, golferid: string, video_id: string) {
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(environment),
        "golfers",
        golferid,
        "videos"
      ),
      video_id
    );
    deleteDoc(docRef).catch((e) => console.log(e));
  }

  deleteGolfer(environment: string, golferid: string) {
    const docRef = doc(
      collection(UserFirebaseService.getFirestore(environment), "golfers"),
      golferid
    );
    deleteDoc(docRef).catch((e) => console.log(e));
  }

  setAbility(environment: string, firestoreid: string, ability: string) {
    const docRef = doc(
      collection(UserFirebaseService.getFirestore(environment), "golfers"),
      firestoreid
    );
    setDoc(docRef, { ability: ability }, { merge: true });
  }

  updateGolferName(environment: string, golferid: string, name: string) {
    const docRef = doc(
      collection(UserFirebaseService.getFirestore(environment), "golfers"),
      golferid
    );
    setDoc(docRef, { name: name }, { merge: true });
  }

  getLiveFeedbackImage(livefeedbackid: string, imageindex: number): Promise<any> {
    return new Promise((resolve) => {
      // Create a reference to the 'golfers' collection
      const liveFeedbackCollection = collection(
        UserFirebaseService.getFirestore("default"),
        "live_feedback"
      );
    
      // Create a reference to the specific image document
      const liveFeedbackDocRef = doc(liveFeedbackCollection, livefeedbackid);

      // Get the document
      getDoc(liveFeedbackDocRef).then((docSnap) => {
        resolve({
          "keypoints": docSnap.data().keypoints[imageindex],
          "annotated_keypoints": docSnap.data().annotated_keypoints_by_image?docSnap.data().annotated_keypoints_by_image[imageindex]:{},
          "image_path": "live_feedback/"+docSnap.data().file_names[imageindex],
          "ref": liveFeedbackDocRef,
          "keypoint_discrepancies": docSnap.data().keypoint_discrepancies
        });
      });
    });
  }

  getImage(
    environment: string,
    golferid: string,
    videoid: string,
    docid: string
  ): Promise<any> {
    return new Promise((resolve) => {
      // Create a reference to the 'golfers' collection
      const golfersCollection = collection(
        UserFirebaseService.getFirestore(environment),
        "golfers"
      );

      // Create a reference to the specific golfer document
      const golferDocRef = doc(golfersCollection, golferid);

      // Create a reference to the 'videos' subcollection
      const videosCollection = collection(golferDocRef, "videos");

      // Create a reference to the specific video document
      const videoDocRef = doc(videosCollection, videoid);

      // Create a reference to the 'images' subcollection
      const imagesCollection = collection(videoDocRef, "images");

      // Create a reference to the specific image document
      const imageDocRef = doc(imagesCollection, docid);

      // Get the document
      getDoc(imageDocRef).then((docSnap) => {
        resolve(UserFirebaseService.convertDocToImage(environment, docSnap));
      });
    });
  }

  getVideo(environment: string, golferid: string, docid: string): Promise<any> {
    return new Promise((resolve) => {
      // Initialize Firestore
      const firestore = UserFirebaseService.getFirestore(environment);

      // Create a reference to the 'golfers' collection
      const golfersCollection = collection(firestore, "golfers");

      // Create a reference to the specific golfer document
      const golferDocRef = doc(golfersCollection, golferid);

      // Create a reference to the 'videos' subcollection
      const videosCollection = collection(golferDocRef, "videos");

      // Create a reference to the specific video document
      const videoDocRef = doc(videosCollection, docid);

      // Get the document
      getDoc(videoDocRef).then((docSnap) => {
        resolve(UserFirebaseService.convertDocToVideo(environment, docSnap));
      });
    });
  }

  getVideoInternal(
    environment: string,
    golferid: string,
    docid: string
  ): Promise<any> {
    return new Promise((resolve) => {
      // Initialize Firestore
      const firestore = UserFirebaseService.getFirestore(environment);

      // Create a reference to the 'golfers' collection
      const golfersCollection = collection(firestore, "golfers");

      // Create a reference to the specific golfer document
      const golferDocRef = doc(golfersCollection, golferid);

      // Create a reference to the 'videos' subcollection
      const videosCollection = collection(golferDocRef, "videos");

      // Create a reference to the specific video document
      const videoDocRef = doc(videosCollection, docid);

      // Create a reference to the 'internal' subcollection
      const internalCollection = collection(videoDocRef, "internal");

      // Create a reference to the specific internal document
      const internalDocRef = doc(internalCollection, "internal");

      // Get the document
      getDoc(internalDocRef).then((docSnap) => {
        console.log(
          "got video internal",
          environment,
          golferid,
          docid,
          docSnap
        );
        resolve(
          UserFirebaseService.convertDocToVideoInternal(environment, docSnap)
        );
      });
    });
  }

  setName(environment: string, firestoreid: string, name: string) {
    const docRef = doc(
      collection(UserFirebaseService.getFirestore(environment), "golfers"),
      firestoreid
    );
    setDoc(docRef, { name: name }, { merge: true });
  }

  setGender(environment: string, firestoreid: string, gender: string) {
    const docRef = doc(
      collection(UserFirebaseService.getFirestore(environment), "golfers"),
      firestoreid
    );
    setDoc(docRef, { gender: gender }, { merge: true });
  }

  setDiscardReason(
    environment: string,
    firestoreid: string,
    golferid: string,
    discardReason: number,
    line: number = -1,
    frame: number = -1
  ) {
    const newDiscard = {};
    newDiscard[firestoreid] = {
      frame: frame,
      reason: discardReason,
      email: UserFirebaseService.auth.currentUser.email,
      time: new Date().toLocaleString(),
      line: line,
      golferid: golferid,
    };
    const docRef = doc(
      collection(UserFirebaseService.getFirestore(environment), "website"),
      "discards"
    );
    setDoc(docRef, newDiscard, { merge: true });
  }

  reprocessImage(
    environment: string,
    golferid: string,
    videoid: string,
    docid: string
  ) {
    const docRef = doc(
      collection(
        UserFirebaseService.getFirestore(environment),
        "golfers",
        golferid,
        "videos",
        videoid,
        "images"
      ),
      docid
    );
    setDoc(
      docRef,
      { processed: 0, processed_changed: new Date().toISOString() },
      { merge: true }
    );
  }

  setTitle(
    environment: string,
    golferid: string,
    videoid: string,
    name: string
  ) {
    if (name) {
      const docRef = doc(
        collection(
          UserFirebaseService.getFirestore(environment),
          "golfers",
          golferid,
          "videos"
        ),
        videoid
      );
      setDoc(docRef, { title: name }, { merge: true });
    }
  }

  deleteStoredMedia(name: string, bucket: string = "ninelines-media") {
    UserFirebaseService.storageService
      .refFromURL("gs://" + bucket)
      .child(name)
      .delete();
  }

  async getErrors(
    metricFriendlyId: string,
    metricFirestoreid: string,
    truth: string,
    machine: string,
    machineCont: number
  ) {
    if (truth == machine) return [0, 0];
    //get the metric
    let metric = null;
    if (this.metrics) {
      metric = this.metrics.filter((m) => m.id == metricFriendlyId)[0];
      console.log("metric 1", metric);
    }
    if (!metric) {
      this.getMetricsSubject(); //trigger an initialization for next time
      console.log("getErrors", metricFirestoreid);
      metric = await this.getMetricFirestoreId(metricFirestoreid);
      console.log("metric 2", metric);
    }

    if (!metric) {
      Sentry.captureMessage(
        "getErrors metric not found " +
          metricFriendlyId +
          " " +
          metricFirestoreid
      );
    }

    const rules = metric.rules;
    //want index of rule in rules corresponding to truth
    const truthIndex = rules.map((r) => r.id).indexOf(truth);
    const machineIndex = rules.map((r) => r.id).indexOf(machine);
    console.log("indexes", truthIndex, machineIndex);
    if (truthIndex == -1) {
      //send an error, including truth
      //sentry
      Sentry.captureMessage(
        "Invalid truth index for " + truth + " for " + metricFriendlyId
      );
      return [0, 0];
    }
    const error = Math.abs(truthIndex - machineIndex);
    const error_cont = Math.min(
      Math.abs(rules[truthIndex].maxValue - machineCont),
      Math.abs(rules[truthIndex].minValue - machineCont)
    );
    return [error, error_cont];
  }

  getRecentQuizResults() {
    //Call https://us-central1-ninelines-254623.cloudfunctions.net/get-recent-quiz-answers (no parameters needed)
    const url =
      "https://us-central1-ninelines-254623.cloudfunctions.net/get-recent-quiz-answers";
    return fetch(url)
      .then((response) => response.json())
      .then((data) => {
        console.log("recent quiz results", data);
        return data;
      });
  }

  async sendAmplitudeEvent(
    userName: string,
    category: string,
    result: string,
    machine: string,
    video_id: string,
    machineCont: number,
    processedChanged: number //can't think why we would use this, instead of just using current time?
  ) {
    if (!this.metricFriendlyIdToFirestoreId) {
      await this.populateMetricsFriendlyIdToFirestoreId();
    }
    const lookupEmailToName = {
      "info@mysportingspace.com": "Gav",
      "hamlyn5@hotmail.com": "Gav",
      "maltongue@xtra.co.nz": "Mal",
    };
    const friendlyUserName: string = lookupEmailToName[userName]
      ? lookupEmailToName[userName]
      : userName;

    //    const amplitude_url = "https://api2.amplitude.com/batch";
    const amplitude_key = process.env.AMPLITUDE_KEY;

    //get metric_id from the category
    const metric_id = this.metricFriendlyIdToFirestoreId["m_" + category];

    //get error and error_cont
    let error = 0;
    let error_cont = 0;

    try {
      [error, error_cont] = await this.getErrors(
        "m_" + category,
        metric_id,
        result,
        machine,
        machineCont
      );
    } catch (e) {
      console.error("Error in getErrors:", e);
      error = 0; // Default value
      error_cont = 0; // Default value
    }

    const data = {
      api_key: amplitude_key,
      events: [
        {
          event_type: "quiz_result",
          //time as an epoch in milliseconds
          time: processedChanged ? processedChanged : new Date().getTime(),
          user_id: friendlyUserName,
          event_properties: {
            metric_id: metric_id,
            machine: machine,
            truth: result,
            video_id: video_id,
            error: error,
            error_cont: error_cont,
          },
        },
      ],
    };

    //now actually send it using fetch API
    // fetch(amplitude_url, {
    // method: 'POST',
    // headers: {
    //     'Content-Type': 'application/json',
    //     'Accept': '*/*'
    // },
    // body: JSON.stringify(data)
    // })
    // .then(response => response.json())
    // .then(data2 => console.log('amplitude response', data2))
    // .catch(error => console.log('amplitude error', error));

    const gcfUrl =
      "https://us-central1-ninelines-254623.cloudfunctions.net/log-quiz-event";

    const data2 = {
      event_type: "quiz_result",
      time: processedChanged ? processedChanged : new Date().getTime(),
      user_id: friendlyUserName,
      event_properties: {
        metric_id: metric_id,
        machine: machine,
        truth: result,
        video_id: video_id,
        error: error,
        error_cont: error_cont,
      },
    };

    console.log("sending event to GCF", data);

    fetch(gcfUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify(data2),
    })
      .then((response) => response.json())
      .then((data) => console.log(data))
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  answerQuizQuestion(
    environment: string,
    golferid: string,
    firestoreid: string,
    category: string,
    result: string,
    machine: string,
    machineCont: number,
    video_truth: Object,
    authority: boolean = false,
    userEmail: string = null,
    allowCancels: boolean = false,
    processedChanged: number = 0 //can't think why we would use this...it ultimately indicates what goes into quiz_event, and changes the default from today's datetime
  ) {
    //Check result is in the category
    if (
      (!allowCancels || result != null) &&
      result != "na" &&
      !(
        this.questions &&
        this.questions.filter((q) => q["radio_header"] == category).length >
          0 &&
        this.questions
          .filter((q) => q["radio_header"] == category)[0]
          ["answers"].filter((x) => x["value"] == result).length > 0
      )
    ) {
      console.log(
        "quiz answer not valid " +
          category +
          " " +
          result +
          " " +
          golferid +
          " " +
          firestoreid
      );
      console.log("questions", this.questions);
      if (this.questions)
        console.log(
          "filtered questions",
          this.questions.filter((q) => q["radio_header"] == category)
        );
      Sentry.captureMessage(
        "quiz answer not valid " +
          category +
          " " +
          result +
          " " +
          golferid +
          " " +
          firestoreid
      );
    }
    //if reuslt is undefined, should mean they have deselected radio button...hard to handle, so just disable this ability perhaps
    console.log(firestoreid, category, result);
    if (result || allowCancels) {
      // if it is 2charlesj@gmail.com then set to auditor
      if (userEmail === '2charlesj@gmail.com') {
        userEmail = 'auditor';
      }
      const userName = userEmail
        ? userEmail
        : authority
        ? "authority"
        : UserFirebaseService.auth.currentUser.email;

        

      //Need to see if there is a current result
      let times: number = 1;
      if (video_truth) {
        //check for existing results
        const existing_result =
          video_truth &&
          video_truth["quiz"] &&
          video_truth["quiz"][userName] &&
          video_truth["quiz"][userName][category]
            ? video_truth["quiz"][userName][category]
            : null;
        if (existing_result) {
          if (existing_result == result) {
            const existing_times =
              video_truth &&
              video_truth["quiz"] &&
              video_truth["quiz"][userName] &&
              video_truth["quiz"][userName][category + "_times"]
                ? video_truth["quiz"][userName][category + "_times"]
                : 1;
            times = existing_times + 1;
          }
        }
      }

      if (category[0] == "_" && category[1] == "_")
        Sentry.captureMessage("quiz category has m_");

      const truth = {};
      truth["quiz"] = {};
      truth["quiz"][userName] = {};
      truth["quiz"][userName][category] = result ? result : "";
      truth["quiz"][userName][category + "_time"] = new Date().toLocaleString();
      truth["quiz"][userName][category + "_times"] = times;
      const internalDocRef = doc(
        collection(
          UserFirebaseService.getFirestore(environment),
          "golfers",
          golferid,
          "videos",
          firestoreid,
          "internal"
        ),
        "internal"
      );
      console.log("setting doc", internalDocRef, truth);
      setDoc(internalDocRef, { truth: truth }, { merge: true }).then(
        (result) => {
          console.log("set doc result", result);
        }
      );

      //Send an amplitude event
      if (machine)
        this.sendAmplitudeEvent(
          userName,
          category,
          result,
          machine,
          firestoreid,
          machineCont,
          processedChanged
        );
    }
  }

  initQuizMetricsSubject() {
    if (this.quizMetricsSubjectInitialized) return;
    this.quizMetricsSubjectInitialized = true;
    UserFirebaseService.userSubject.subscribe(() => {
      if (UserFirebaseService.snapshots["quizMetrics"])
        UserFirebaseService.snapshots["quizMetrics"]();
      console.log("about to query metrics_internal for quizzes");
      const metricsInternalCollection = collection(
        UserFirebaseService.firestore_swee_prod,
        "metrics_internal"
      );
      const q = query(metricsInternalCollection, where("quiz", "==", true));
      UserFirebaseService.snapshots["quizMetrics"] = onSnapshot(
        q,
        (querySnapshot) => {
          console.log("got snapshot from metrics_internal for quizzes");
          const questions = [];
          querySnapshot.docs.forEach((doc) => {
            const docdata = doc.data();
            if (docdata["link"] && docdata["link"]["logic"]) return;

            //if('exclude' in docdata && docdata['exclude']) return
            //{"angle":90,"line":0,"radio_header":"shoulder_alignment","label":"Shoulder alignment","answers":[{"color":"danger","value":"too_open","label":"Too open"},{"color":"warning","value":"slightly_too_open","label":"Slightly too open"},{"color":"success","value":"perfect","label":"About right"},{"color":"warning","value":"slightly_too_closed","label":"Slightly too closed"},{"color":"danger","value":"too_closed","label":"Too closed"}]},
            const question = {
              club: docdata["club"],
              angle: docdata["video_angle"],
              line: docdata["line"],
              radio_header: docdata["id"].substring(2),
              label: capitalizeFirstLetter(docdata["name"]),
              answers: docdata["rules"].map((r) => {
                return {
                  color:
                    r["penalty"] == 1
                      ? "danger"
                      : r["penalty"] == 0.5
                      ? "warning"
                      : "success",
                  value: r["id"],
                  label: capitalizeFirstLetter(r["text"]),
                };
              }),
            };
            if ("contrastLine" in docdata)
              question["contrastLine"] = docdata["contrastLine"];
            if ("accuracy" in docdata)
              question["accuracy"] = docdata["accuracy"];

            questions.push(question);
          });
          this.questions = questions;
          this.quizMetricsSubject.next(this.questions);
          console.log("updated quiz questions");
        },
        (error) => {
          Sentry.captureException(error);
          Sentry.captureMessage("quizMetrics error");
          console.log("quiz metrics error", error);
        }
      );
    });
  }

  public getAvailableVideos(
    videos,
    questionAngle,
    radioHeader,
    discards,
    userTruths,
    club
  ) {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 14);
    const oneWeekAgoString = oneWeekAgo.toISOString();

    //sort videos randomly
    let filterVideos = videos.filter(
      (x: Video) =>
        x.score >= 0 &&
        //only SWEE ones
        x.environment == "prod" &&
        //exclude SWEE TEST in name
        !(x.name && x.name.includes("SWEE TEST")) &&
        !(
          x.errors &&
          x.errors.length > 0 &&
          ((x.errors[0] as string).includes("Couldn't find a person") ||
            (x.errors[0] as string).includes("has been deleted") ||
            (x.errors[0] as string).includes("Couldn't find a golf swing") ||
            x.errors.includes("Couldn't find a person"))
        ) &&
        (!(x.errors && x.errors.length > 0) || x.video_angle == 90) &&
        x.video_angle == questionAngle &&
        x.firestoreid != "hSce6TGnUp7V4Qsbv1nD" &&
        x.golferid != "l2o1cMQlpncPiZgocyWc" &&
        x.golferid != "pi4WwQAT3zAm3lN7vToW" &&
        !(x.name && x.name.includes("2457dd")) &&
        !(x.title && x.title.includes("SpeedTest")) &&
        !(x.title && x.title.includes("MySwing")) &&
        !discards.has(x.firestoreid) &&
        !(
          (x.club ?? x.clubPrediction) &&
          (x.club ?? x.clubPrediction) != "iron" &&
          club == "iron"
        ) &&
        !(
          club == "driver" &&
          (!(x.club ?? x.clubPrediction) ||
            (x.club ?? x.clubPrediction) != "driver")
        ) && //if club=='driver' needs to have x.club=='driver', can't be empty or null
        !(
          userTruths[radioHeader] &&
          userTruths[radioHeader].includes(x.firestoreid)
        )
    );

    return filterVideos
      .filter(
        (x: Video) =>
          filterVideos.length < 500 || x.upload_date > oneWeekAgoString
      )
      .sort(() => Math.random() - 0.5);
  }
}

export const AdminFirebaseService = new AdminFirebaseController();
